import Icon from "components/Common/Icon";
import { MediaQuery } from "@outplayed/responsive";
import { getWowStatic } from "hooks/wow/getStaticData";
import { getEnchantName, getEnchantQuality, getEnchantQualityIconSrc, getEnchantImgLinkSrc, trimQuality } from "lib/enchant-ids";
import { getFormattedContentType, getFormattedNames } from "lib/wowHelpers";
import React from "react";
import SubtitleAndDescription from "../Common/SubtitleAndDescription";
import WowTooltip from "../Tooltips/WowTooltip";
import ArmorItemCombo from "./ArmorItemCombo";
import WeaponItem from "./WeaponItem";

const PvPGearGuide = (props) => {
  const { classType, specType, encounterType } = props;
  const { displayClassName, displaySpecName } = getFormattedNames(classType, specType);

  const specClassName = `${displaySpecName} ${displayClassName}`;
  const contentType = getFormattedContentType(encounterType);

  return (
    <div className="wow-items-section content-section">
      <div className="header-section">
        <div className="title-and-button">
          <SubtitleAndDescription
            subtitle={`BIS ${specClassName} PvP Gear`}
            description={`The best in slot PvP gear used by the best ${specClassName} players. Gear in each slot is determined by analyzing the top ${specClassName} ${contentType} rankings. The selected piece of gear is the most popular item for that slot.`}
          />
        </div>
      </div>
      <WOWArmorLayoutContainer {...props} />
      <WOWWeaponLayoutContainer {...props} />
    </div>
  );
};

const WOWArmorLayoutContainer = (props) => {
  const firstColumnItems = ["head", "neck", "shoulder", "cape", "chest", "shirt", "tabard", "wrist"];
  const secondColumnItems = ["gloves", "belt", "legs", "feet"];
  const combos = ["ring1_combos", "trinket1_combos"];

  return (
    <div className="armor-layout-container">
      <WOWArmorLayoutColumn {...props} itemSlots={firstColumnItems} isLeftSide />
      <WOWArmorLayoutColumn {...props} itemSlots={secondColumnItems} comboProperties={combos} />
    </div>
  );
};

const WOWArmorLayoutColumn = (props) => {
  const { builds, itemSlots, isLeftSide, combos, comboProperties } = props;

  const setPieces = builds
    ? Object.values(builds)
        .map((slotData) => slotData?.item)
        .filter((itemId) => itemId > 0)
        .join(":")
    : "";

  const itemData = itemSlots.map((slot) => {
    if (!builds) {
      return {
        slot,
      };
    }

    const itemInSlot = builds[slot];

    if (itemInSlot) {
      return {
        id: itemInSlot.item,
        quality: itemInSlot.quality,
        slot,
        popPerc: parseFloat(itemInSlot.popularity).toFixed(1) + "%",
        popCount: itemInSlot.count,
        enchant: itemInSlot.enchant_name,
        enchantId: itemInSlot.enchant,
        bonusIds: itemInSlot.bonus_ids,
        craftingQuality: itemInSlot.crafted_quality,
        gems: itemInSlot.gems,
      };
    }

    return {
      slot,
    };
  });

  return (
    <div className="armor-layout">
      {itemData.map((item, index) => {
        return (
          <ArmorItem
            key={index}
            id={item.id}
            isLeftSide={isLeftSide}
            // itemColor={getItemQualityColor(item.quality)}
            itemColor={item.quality === 5 ? "#ff8000" : "rgb(163, 53, 238)"}
            popPerc={item.popPerc}
            popCount={item.popCount}
            enchantName={item.enchant}
            enchantId={item.enchantId}
            bonusIds={item.bonusIds}
            craftingQuality={item.craftingQuality}
            setPieces={setPieces}
            gems={item.gems}
            slot={item.slot}
            {...props}
          />
        );
      })}
      {comboProperties?.map((property, index) => {
        return <ArmorItemCombo key={index} data={combos[property]} encounterType="PvP" />;
      })}
    </div>
  );
};

const ArmorItem = (props) => {
  const { isLeftSide, id, popPerc, popCount, enchantName, enchantId, slot, bonusIds, craftingQuality, setPieces, gems } = props;
  let { itemName = "Cosmetic", itemColor, className } = props;

  const staticData = getWowStatic("item", id);
  let img;
  if (!staticData || !staticData.name) {
    if (slot === "shirt" || slot === "tabard") {
      img = `https://static.bigbrain.gg/wow/icons/empty-slots/${slot}.png`;
    } else {
      itemName = "";
    }

    itemColor = "#717171";
    className = "empty-slot";
  } else {
    itemName = staticData.name;
    img = staticData.img;
  }

  return (
    <div className={isLeftSide ? `armor-item ${className}` : `armor-item-reversed ${className}`} item-id={id}>
      <div className="armor-item-container">
        <WowTooltip
          type="item"
          id={id}
          bonusIds={bonusIds}
          craftingQuality={craftingQuality}
          pcs={setPieces}
          enchant={enchantId}
          gems={gems}
        >
          <Icon staticDataImg={img} color={itemColor} classType="bis-item-icon" />
        </WowTooltip>
        <div className={isLeftSide ? "item-info" : "item-info-right"}>
          <div className="item-name" style={{ color: itemColor }}>
            {itemName}
          </div>
          <div className="enchant-info">
            {enchantName ? (
              <Icon iconSize="18px" staticDataImg={getEnchantImgLinkSrc(enchantName?.replace(/&/g, "&amp;"))} />
            ) : null}
            <div className="enchant-text">{trimQuality(getEnchantName(enchantName?.replace(/&/g, "&amp;")))}</div>
            {enchantName ? (
              <img
                style={
                  getEnchantQuality(enchantName) == 2
                    ? { width: "16px", height: "8px", marginLeft: "4px" }
                    : { width: "16px", height: "12px", marginLeft: "4px" }
                }
                src={getEnchantQualityIconSrc(enchantName)}
              ></img>
            ) : null}
          </div>
        </div>
      </div>
      {itemName !== "Cosmetic" && (
        <div className="popularity-container">
          <div className="popularity-value">{popPerc}</div>
          <div className="popularity-label">({popCount?.toLocaleString("en-US")} players)</div>
        </div>
      )}
    </div>
  );
};

const WOWWeaponLayoutContainer = (props) => {
  const { combos } = props;

  const weapons = combos?.weapon1_combos;

  if (!weapons) return null;

  const weapon1Data = getWowStatic("item", weapons.first_item_id);
  const weapon2Data = getWowStatic("item", weapons.second_item_id); // const weapon2Img = weaponSlot2?.img;

  if (!weapon2Data) {
    return (
      <>
        <div className="weapon-combos-label">Weapon: Main Hand</div>
        <div className="weapon-layout-container">
          <div className="weapons">
            <WeaponItem
              positionLeft
              itemName={weapon1Data?.name}
              // itemColor={getItemQualityColor(weaponSlot1.quality)}
              itemColor={weapons.first_item_quality.toLowerCase() === "legendary" ? "#ff8000" : "rgb(163, 53, 238)"}
              img={weapon1Data?.img}
              id={weapons.first_item_id}
              enchantName={weapons.first_item_enchant_name}
              enchantId={weapons.first_item_enchant}
              bonusIds={weapons.first_item_bonus_ids}
              craftingQuality={weapons.first_item_crafted_quality}
              gems={weapons.first_item_gems}
              popularity={weapons.popularity}
              count={weapons.count}
            />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="weapon-combos-label">Weapons: Main Hand & Off Hand</div>
      <div className="weapon-layout-container weapon-combos">
        <div className="weapons">
          <WeaponItem
            positionLeft
            itemName={weapon1Data?.name}
            // itemColor={getItemQualityColor(weaponSlot1.quality)}
            itemColor={weapons.first_item_quality.toLowerCase() === "legendary" ? "#ff8000" : "rgb(163, 53, 238)"}
            img={weapon1Data?.img}
            id={weapons.first_item_id}
            enchantName={weapons.first_item_enchant_name}
            enchantId={weapons.first_item_enchant}
            bonusIds={weapons.first_item_bonus_ids}
            craftingQuality={weapons.first_item_crafted_quality}
            gems={weapons.first_item_gems}
          />
          <MediaQuery min="MOBILE_LARGE" max="DESKTOP_LARGE" renderNullOnFail>
            <div className="popularity-container">
              <div className="popularity-value">{parseFloat(weapons.popularity).toFixed(1)}%</div>
              <div className="popularity-label">({weapons.count.toLocaleString("en-US")} players)</div>
            </div>
          </MediaQuery>
          <MediaQuery min="MOBILE_SMALL" max="MOBILE_MEDIUM" renderNullOnFail>
            <div className="horizontal-line" />
          </MediaQuery>
          <WeaponItem
            itemName={weapon2Data?.name}
            // itemColor={getItemQualityColor(weaponSlot1.quality)}
            itemColor={weapons.second_item_quality.toLowerCase() === "legendary" ? "#ff8000" : "rgb(163, 53, 238)"}
            img={weapon2Data?.img}
            id={weapons.second_item_id}
            enchantName={weapons.second_item_enchant_name}
            enchantId={weapons.second_item_enchant}
            bonusIds={weapons.second_item_bonus_ids}
            craftingQuality={weapons.second_item_crafted_quality}
            gems={weapons.second_item_gems}
          />
        </div>
        <MediaQuery min="MOBILE_SMALL" max="MOBILE_MEDIUM" renderNullOnFail>
          <div className="popularity-container">
            <div className="popularity-value">{parseFloat(weapons.popularity).toFixed(1)}%</div>
            <div className="popularity-label">({weapons.count.toLocaleString("en-US")} players)</div>
          </div>
        </MediaQuery>
      </div>
    </>
  );
};

export default PvPGearGuide;
